/**
 * Paper size for challan printing.
 *
 * Challans were always laid out for A5 — portrait for the delivery challan, landscape
 * for the purchase challan. A super admin can now switch a document to A4 from the
 * settings page; A5 stays the default so nothing changes until someone opts in.
 *
 * How the override works
 * ----------------------
 * Every challan print builds a self-contained HTML document with its own <style>
 * block that already declares the A5 rules. Rather than restructure five of those
 * blocks, `challanPrintOverrideCss` returns a small block appended at the END of the
 * existing styles. Same selectors, later in the sheet, so it simply wins — and when
 * the size is A5 it emits nothing at all, which means the default path renders the
 * exact bytes it did before this feature existed.
 *
 * Scaling
 * -------
 * The layouts use absolute px font sizes, so putting an A5 design on A4 unchanged
 * would leave small type marooned in the top-left of a big sheet. The container keeps
 * its A5 design width and is scaled up as a unit with `zoom`, which is how you'd
 * enlarge the same artwork onto bigger paper. Zoom factors are chosen so the scaled
 * width lands just inside the A4 printable area:
 *
 *   portrait  148mm design -> 210mm sheet - 2x10mm margin = 190mm printable; 148 * 1.28 = 189mm
 *   landscape 210mm design -> 297mm sheet - 2x6mm  margin = 285mm printable; 210 * 1.35 = 283mm
 *
 * A browser without `zoom` support still prints a correct, merely unscaled, A4 page.
 */

export const PAPER_SIZES = { A5: 'A5', A4: 'A4' };

/** What challans printed at before this setting existed. */
export const DEFAULT_PAPER_SIZE = PAPER_SIZES.A5;

/** Document keys — must match VALID_DOCUMENT_TYPES in app/crud/print_setting.py. */
export const PRINT_DOCUMENTS = {
  DELIVERY_CHALLAN: 'delivery_challan',
  PURCHASE_CHALLAN: 'purchase_challan',
};

const A4_PROFILES = {
  portrait: { page: 'A4', margin: '10mm', designWidth: '148mm', zoom: 1.28 },
  landscape: { page: 'A4 landscape', margin: '6mm', designWidth: '210mm', zoom: 1.35 },
};

/**
 * Normalize whatever the API/browser handed us to a size we have a profile for.
 * Anything unrecognised falls back to A5 rather than producing a broken page.
 */
export const normalizePaperSize = (value) => {
  const key = String(value || '').trim().toUpperCase();
  return key === PAPER_SIZES.A4 ? PAPER_SIZES.A4 : PAPER_SIZES.A5;
};

/**
 * CSS to append at the end of a challan's <style> block.
 *
 * @param {string} paperSize     'A5' | 'A4' (anything else is treated as A5)
 * @param {string} orientation   'portrait' (delivery challan) | 'landscape' (purchase)
 * @returns {string} CSS, or '' for A5 — the layouts already declare A5 themselves.
 */
export const challanPrintOverrideCss = (paperSize, orientation = 'portrait') => {
  if (normalizePaperSize(paperSize) !== PAPER_SIZES.A4) return '';

  const profile = A4_PROFILES[orientation === 'landscape' ? 'landscape' : 'portrait'];
  return `
  /* Paper size override — configured in Settings. Appended after the layout's own
     A5 rules so these win by source order. */
  @page { size: ${profile.page}; margin: ${profile.margin}; }
  .print-container {
    max-width: ${profile.designWidth};
    zoom: ${profile.zoom};
  }
  @media print {
    .print-container { zoom: ${profile.zoom}; }
  }`;
};
